import {
  Camera,
  CameraCreationStatusData,
  CameraListItem,
  CameraSource,
  ImportCameraData,
  MilestoneCameraAttributes
} from '@/types/camera'
import { EventDestination } from '@/types/deployment'
import { PaginatedResponse, WithListParams } from '@/types/pagination'
import { vpApi } from './vpApi'

export type CameraListParams = WithListParams<{
  device_id?: string
  status?: string
  text_search?: string
}>

export const getCameraList = async (params: CameraListParams) => {
  const res = await vpApi.get<PaginatedResponse<CameraListItem[]>>(
    `/v1/devices/cameras/`,
    {
      params
    }
  )

  return res.data
}

type CameraParams = {
  deviceId: string
  cameraId: string
}

const getCameraDetails = async ({ deviceId, cameraId }: CameraParams) => {
  const res = await vpApi.get<Camera>(
    `/v1/devices/${deviceId}/cameras/${cameraId}/`
  )
  return res.data
}

type CreateCamerasParams = {
  deviceId: string
  cameras: {
    camera_name: string
    rtsp_url: string
    source: CameraSource
    attributes?: MilestoneCameraAttributes
  }[]
}

const createCameras = ({ deviceId, cameras }: CreateCamerasParams) => {
  return vpApi.post(`/v1/devices/${deviceId}/cameras/`, cameras)
}

const getCameraCreationStatus = async (deviceId: string) => {
  const res = await vpApi.get<CameraCreationStatusData>(
    `/v1/devices/${deviceId}/cameras/camera-addition-status/`
  )
  return res.data
}

const deleteCamera = ({ deviceId, cameraId }: CameraParams) => {
  return vpApi.delete(`/v1/devices/${deviceId}/cameras/${cameraId}/`)
}

type CameraTaskResponse = {
  task_id: string
  camera_id: string
}

const refreshCameraImage = async ({ deviceId, cameraId }: CameraParams) => {
  const res = await vpApi.put<CameraTaskResponse>(
    `/v1/devices/${deviceId}/cameras/${cameraId}/camera-screenshot/`
  )
  return res.data
}

const pingCamera = async ({ deviceId, cameraId }: CameraParams) => {
  const res = await vpApi.put<CameraTaskResponse>(
    `/v1/devices/${deviceId}/cameras/${cameraId}/ping-camera/`
  )
  return res.data
}

type GetImportCamerasParams = {
  deviceId: string
  integrationUrl: string
  source: CameraSource
}

type GetImportCamerasResponse = {
  cameras: ImportCameraData[]
}

const getImportCameras = async ({
  deviceId,
  integrationUrl,
  source
}: GetImportCamerasParams) => {
  const res = await vpApi.post<GetImportCamerasResponse>(
    `/v1/devices/${deviceId}/cameras/integration-streams/`,
    {
      integration_url: integrationUrl,
      source
    }
  )

  return res.data
}

type CheckIntegrationUrlParams = {
  deviceId: string
  integrationUrl: string
  source: EventDestination
}

type CheckIntegrationUrlResponse = {
  success: boolean
  detail?: string
}

const checkIntegrationUrl = async ({
  deviceId,
  integrationUrl,
  source
}: CheckIntegrationUrlParams) => {
  const res = await vpApi.post<CheckIntegrationUrlResponse>(
    `/v1/devices/${deviceId}/cameras/integration-endpoint-check/`,
    {
      integration_url: integrationUrl,
      source
    }
  )

  return res.data
}

export const cameraApi = {
  getCameraList,
  getCameraDetails,
  createCameras,
  getCameraCreationStatus,
  deleteCamera,
  refreshCameraImage,
  pingCamera,
  getImportCameras,
  checkIntegrationUrl
}
